/**
 * Homingo colour palette.
 *
 * Source of truth is the "Color Palette" board in the design file - six
 * swatches, one hue, dark to light. Those six hex values are reproduced
 * verbatim in `palette` below and are the ONLY greens in the app.
 *
 * Previous versions of this file sampled colours out of rendered mockup
 * images, which carried a warm lighting cast and produced olive-tinted
 * greens that do not exist in the spec. Do not re-sample from renders.
 *
 * White and black are the only additions - the spec has no card white and
 * no shadow colour, and neither can be expressed with the six.
 */

/** Raw brand ramp, verbatim from the palette board. Prefer `colors` below. */
export const palette = {
  /** #0E2A1F - deepest forest. Headings, dark cards, pressed fills. */
  green900: '#0E2A1F',
  /** #1D4E35 - brand green. Filled buttons, active icons, the logo. */
  green800: '#1D4E35',
  /** #3F6F4B - mid green. Secondary copy, success states. */
  green600: '#3F6F4B',
  /**
   * #6E9077 - muted green. The one step between `green600` and `green300`.
   *
   * Added for disabled TEXT, which had nowhere to go: `green600` is the same
   * ink as ordinary secondary copy, so a dead control looked live, and
   * `green300` is 1.7:1 on a light surface, so the alternative was invisible.
   * Measured: 3.3:1 on `green50`, 3.6:1 on white, 3.1:1 on `green100` - over
   * the 3:1 floor on every surface this app puts text on, and visibly lighter
   * than the copy around it.
   */
  green500: '#6E9077',
  /** #A7C5A6 - sage. Outlines, muted copy on dark, decorative fills. */
  green300: '#A7C5A6',
  /** #E8F0E6 - pale wash. Chips, secondary buttons, hairlines. */
  green100: '#E8F0E6',
  /** #F5F7F2 - near-white. The app canvas. */
  green50: '#F5F7F2',

  /**
   * #C8453C - the one non-brand hue.
   *
   * Destructive actions need to be unmistakable, and every green in this ramp
   * reads as approval. Used only where something is removed or cleared.
   *
   * Lighter than the #B3261E it started at. That deeper red sat beside the
   * cart bar's forest green as two heavy darks fighting each other; this step
   * still reads as a warning without competing with the bar it hides behind.
   */
  red600: '#C8453C',
  /**
   * #F9EAE9 - the same hue washed almost to the canvas.
   *
   * A tint behind a destructive glyph, mirroring how `green100` sits behind a
   * brand one. Pale enough that a row carrying it still reads as a row rather
   * than as a warning banner.
   */
  red100: '#F9EAE9',

  white: '#FFFFFF',
  black: '#000000',
} as const;

/**
 * Semantic tokens. Components reference these only, never `palette` directly.
 *
 * Contrast against the `background` canvas (#F5F7F2), WCAG AA needs 4.5:1
 * for body text:
 *   green900  15.5:1  ✅   green800  9.4:1  ✅   green600  5.0:1  ✅
 *   green300   1.7:1  ❌ - never put text in green300 on a light surface.
 */
export const colors = {
  /** App canvas. The palette's lightest step, not pure white. */
  background: palette.green50,
  /** Default card / tile surface, lifted off the canvas by being pure white. */
  surface: palette.white,
  /** Elevated surface: search field, floating cards. */
  surfaceRaised: palette.white,
  /** Muted fill: secondary buttons, disabled states, map land. */
  surfaceMuted: palette.green100,
  /** Pale fill: status chips, icon bubbles. */
  surfaceAccent: palette.green100,
  /** Inverted surface: promo banners, dark cards. */
  surfaceInverse: palette.green900,

  /** Display headings. */
  ink: palette.green900,
  /** Primary body copy. */
  inkBody: palette.green800,
  /** Secondary copy, timestamps, helper text. Lightest ink that still passes AA. */
  inkMuted: palette.green600,
  /**
   * Tertiary copy, placeholders, inactive nav labels.
   * Deliberately the same as `inkMuted`: the next step down (green300) fails
   * AA on light surfaces. Use green300 for decoration, never for text.
   */
  inkSubtle: palette.green600,
  /**
   * Text in a control that cannot be pressed - a past date, a slot inside the
   * notice window, the label of a disabled button.
   *
   * Its own token because "disabled" was previously drawn three different
   * ways, and two of them could not be read: `colors.border` (green100, 1.2:1
   * - effectively blank) on past calendar days, and `inkMuted` under an
   * `opacity: 0.4` wrapper, which multiplies down to roughly 2:1. Both were
   * reported as muted elements not being visible.
   *
   * Light enough to read as unavailable at a glance, dark enough to actually
   * read. Use it INSTEAD of an opacity wrapper on text, never on top of one.
   */
  inkDisabled: palette.green500,
  /** Copy on top of `surfaceInverse` / `primary`. */
  inkInverse: palette.white,
  /** Muted copy on top of `surfaceInverse` - sage reads correctly on dark. */
  inkInverseMuted: palette.green300,

  /** Brand green: filled buttons, active icons, links, the logo. */
  primary: palette.green800,
  /** Pressed state for `primary` fills. */
  primaryPressed: palette.green900,
  /** Content on top of `primary`. */
  onPrimary: palette.white,

  /** 1px separators and card outlines. */
  border: palette.green100,
  /** Stronger outline for interactive containers. */
  borderStrong: palette.green300,

  /** Status. The design uses the brand hue for every positive state. */
  success: palette.green600,
  successSurface: palette.green100,
  /** Rating stars are inked, not gold. */
  rating: palette.green800,
  /** Destructive fills - clearing a cart, deleting an address. */
  danger: palette.red600,
  /** Tinted panel behind a destructive glyph. */
  dangerSurface: palette.red100,
  onDanger: palette.white,

  /**
   * WhatsApp's own green, for the one control that hands off to it.
   *
   * A hard-coded hex rather than a palette entry because it is NOT ours: it
   * belongs to somebody else's brand and must not drift with this app's
   * greens. It is here, in the token list, so that the two screens that draw
   * a WhatsApp button cannot each invent their own shade of it.
   *
   * #25D366 on white is about 1.9:1, so it may colour a fill or a glyph and
   * must never be used for copy. `onWhatsapp` is what goes on top.
   */
  whatsapp: '#25D366',
  whatsappPressed: '#1DA851',
  onWhatsapp: palette.white,

  /** Live-tracking map. */
  mapLand: palette.green100,
  mapRoute: palette.green800,

  /** Scrims and shadows. */
  shadow: palette.black,
  overlay: 'rgba(14, 42, 31, 0.45)',
} as const;

export type ColorToken = keyof typeof colors;
